import React, { useState } from "react"
import { Link } from "react-router-dom"
import { FeedSpinner, Spinner } from "../../components/shared/Spinner"
import { RequestError } from "../../components/shared/RequestError"
import {
  SmartDateFormat,
  DateFormat,
  MonthDayYearFormat,
  timeOnly,
} from "../../util/dates"
import { Currency } from "../../util/numbers"
import { PayeeSection } from "./PayeeSection"
import { PlusSquare, PlusSquareFill } from "react-bootstrap-icons"
import { usePayouts } from "../../hooks/usePayouts"
import { useTips } from "../../hooks/useTips"
import { PayoutTicketList } from "./PayoutTicketList"
import Pagination from "../../components/shared/Pagination"
import { InfoTooltip } from "../../components/shared/InfoTooltip"

interface PayoutsProps {
  bandId: number
  payeeId?: number | null
  className?: string
}

interface Payout {
  id: number
  created_at: string
  source_type: "source_tips" | "source_ticket_purchases"
  band_share: number
  total_amount: number
  bn_share: number
  promotion_share: number
  distributed_amount: number
  source_ids: number[]
  user_ids: number[]
  referral_count: number
  status: string
  updated_at: string
  tickets_post_id?: number
  tickets_post_title?: string
  tickets_post_path?: string
  parent_payout_id?: number | null
  sending_band_name?: string | null
}

interface Tip {
  id: number
  tip_date: string
  tipper_name: string
  tipper_email: string
  amount: number
}

export const Payouts: React.FC<PayoutsProps> = ({
  bandId,
  payeeId,
  className = "",
}) => {
  const [page, setPage] = useState<number>(1)
  const { error, data, status, isPreviousData } = usePayouts(bandId, page, 20)

  return (
    <div className={className}>
      <h2 className="lg:hidden font-bold text-xl uppercase border-b-4 border-black mb-4">
        Payouts
      </h2>
      {payeeId && <PayeeSection payeeId={payeeId} />}
      {status === "loading" ? (
        <FeedSpinner />
      ) : status === "error" ? (
        <RequestError error={error} />
      ) : data ? (
        <>
          {data.payouts.length === 0 ? (
            <div>No payouts yet...</div>
          ) : (
            <div>
              <ul
                className={`lg:text-base text-xs ${isPreviousData ? "opacity-50" : ""}`}
              >
                <li className="grid grid-cols-24 font-bold uppercase border-b-2 border-black text-xs leading:none">
                  <div className="col-span-1"></div>
                  <div className="col-span-5">Payout Date</div>
                  <div className="col-span-3">Total</div>
                  <div className="col-span-4">BN Share</div>
                  <div className="col-span-5">Your Share</div>
                  <div className="hidden lg:block col-span-2">Tickets Sold</div>
                  <div className="hidden lg:block col-span-2">
                    Donations Made
                  </div>
                  <div className="col-span-6 lg:col-span-2 text-right">
                    Status
                  </div>
                </li>
                {data.payouts.map((payout: Payout) => (
                  <PayoutListItem payout={payout} key={payout.id} />
                ))}
              </ul>
              <Pagination
                page={data.page}
                totalPages={data.total_pages}
                totalCount={data.total_count}
                hasNextPage={data.has_next_page}
                onPrev={() => setPage((p) => Math.max(1, p - 1))}
                onNext={() => setPage((p) => p + 1)}
              />
            </div>
          )}
        </>
      ) : null}
    </div>
  )
}

const paidAtText = (updatedAt: string): string =>
  `Paid at ${DateFormat(updatedAt)} ${timeOnly({ dateStr: updatedAt })}`

interface PayoutListItemProps {
  payout: Payout
}

const PayoutListItem: React.FC<PayoutListItemProps> = ({ payout }) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const isChild = payout.parent_payout_id != null

  return (
    <>
      <li className="grid grid-cols-24 py-2">
        <div className="col-span-1 flex items-center justify-center">
          {!isChild && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="focus:outline-none"
            >
              {isExpanded ? (
                <PlusSquareFill className="inline-block align-text-top" />
              ) : (
                <PlusSquare className="inline-block align-text-top" />
              )}
            </button>
          )}
        </div>
        <div className="col-span-5">
          {MonthDayYearFormat(payout.created_at)}
          {isChild && payout.sending_band_name && (
            <div className="hidden lg:block text-xs text-green-700">
              {payout.sending_band_name} sent you this payout for:
            </div>
          )}
          {payout.tickets_post_title && (
            <div className="hidden lg:block text-xs text-gray-600">
              {payout.tickets_post_path ? (
                <Link to={payout.tickets_post_path} className="text-inherit">
                  {payout.tickets_post_title}
                </Link>
              ) : (
                payout.tickets_post_title
              )}
            </div>
          )}
        </div>
        <div className="col-span-3">
          {isChild ? "N/A" : Currency(payout.total_amount)}
        </div>
        <div className="col-span-4 text-red-600">
          {isChild ? "N/A" : `-${Currency(payout.bn_share)}`}
        </div>
        <div className="col-span-5 font-bold">
          {Currency(payout.band_share)}
        </div>
        <div className="hidden lg:block col-span-2">
          {isChild ? (
            "N/A"
          ) : payout.source_type === "source_ticket_purchases" ? (
            <>
              {payout.source_ids.length}
              {!!payout.referral_count && (
                <div className="text-xs text-green-700">
                  {payout.referral_count} via{" "}
                  <span className="font-bold">bN</span> referrals
                </div>
              )}
            </>
          ) : (
            "—"
          )}
        </div>
        <div className="hidden lg:block col-span-2">
          {isChild
            ? "N/A"
            : payout.source_type === "source_tips"
              ? payout.source_ids.length
              : "—"}
        </div>
        <div className="col-span-6 lg:col-span-2 text-right text-xs">
          {payout.status === "released" ? (
            <Link
              to={`/dashboard/payouts/${payout.id}`}
              className="text-blue-600 underline font-bold"
            >
              Distribute
            </Link>
          ) : payout.distributed_amount > 0 ? (
            <Link
              to={`/dashboard/payouts/${payout.id}`}
              className="text-gray-600 underline"
            >
              {payout.status === "completed"
                ? paidAtText(payout.updated_at)
                : "Sent"}
            </Link>
          ) : payout.status === "completed" ? (
            paidAtText(payout.updated_at)
          ) : (
            <span className="inline-flex items-center gap-1">
              Pending
              <InfoTooltip text="we will release this for you within 72 hours of the end of the show to distribute as you like" />
            </span>
          )}
        </div>
      </li>
      <hr className="border-gray-300" />
      {isExpanded &&
        (payout.source_type === "source_tips" ? (
          <PayoutTipList payoutId={payout.id} />
        ) : (
          <PayoutTicketList
            payoutId={payout.id}
            showTitle={payout.tickets_post_title}
          />
        ))}
    </>
  )
}

interface PayoutTipListProps {
  payoutId: number
}

const PayoutTipList: React.FC<PayoutTipListProps> = ({ payoutId }) => {
  const { error, data, status } = useTips(payoutId, 1, 100)

  return (
    <li>
      <ul>
        <li className="font-bold">Tip Breakdown</li>
        {status === "loading" ? (
          <Spinner />
        ) : status === "error" ? (
          <RequestError error={error} />
        ) : data ? (
          data.tips.map((tip: Tip) => (
            <li
              key={tip.id}
              className="grid grid-cols-24 py-2 bg-gray-100 text-sm my-1"
            >
              <div className="col-span-1"></div>
              <div className="col-span-6">
                {SmartDateFormat({ dateStr: tip.tip_date })}
              </div>
              <div className="col-span-6">
                {tip.tipper_name} {tip.tipper_email}
              </div>
              <div className="col-span-3 text-right">
                {Currency(tip.amount)}
              </div>
            </li>
          ))
        ) : null}
      </ul>
    </li>
  )
}
